import React, { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import './PlayerDetails.css';

function PlayerDetails({ player, onLookupVehicle }) {
  const { language } = useI18n();
  const [allCharacters, setAllCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehiclePage, setVehiclePage] = useState(1);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [deletingVehicle, setDeletingVehicle] = useState(null);
  const vehiclesPerPage = 10;
  const itemsPerPage = 20;

  const t = language === 'da'
    ? {
        noLicense: 'Ingen license-identifikator fundet',
        failedCharacters: 'Kunne ikke hente karakterer',
        noCharacters: 'Ingen karakterer fundet for denne spiller',
        failedCharacterDetails: 'Kunne ikke hente karakterdetaljer',
        confirmDelete: 'Er du sikker på, at du vil slette køretøj med nummerplade {plate}? Denne handling kan ikke fortrydes!',
        failedDelete: 'Kunne ikke slette køretøj',
        lookupVehicle: 'slå op i køretøj opslag',
        deletedVehicle: 'køretøj {plate} blev slettet',
        title: 'Spillerdetaljer',
        serverId: 'Server ID:',
        name: 'Navn:',
        phoneNumber: 'Telefonnummer:',
        ping: 'Ping:',
        selectCharacter: 'vælg karakter ({count} total)',
        loadingCharacterDetails: 'Indlæser karakterdetaljer...',
        characterInfo: 'Karakterinformation',
        characterSlot: 'Karakterslot:',
        fullName: 'Fulde navn:',
        dob: 'fødselsdato:',
        gender: 'Koen:',
        male: 'Mand',
        female: 'Kvinde',
        height: 'højde:',
        job: 'Job:',
        group: 'Gruppe:',
        money: 'Penge',
        bank: 'Bank',
        cash: 'Kontant',
        blackMoney: 'Sorte penge',
        inventory: 'Inventory ({count} items)',
        previous: 'Forrige',
        next: 'næste',
        pageInfo: 'Side {page} af {total} ({count} total)',
        noItems: 'Ingen items i inventory',
        vehicles: 'Ejede køretøjer ({count})',
        unknownVehicle: 'Ukendt køretøj',
        noPlate: 'INGEN PLADE',
        type: 'Type:',
        status: 'Status:',
        stored: 'Lagret',
        out: 'Ude',
        garage: 'Garage:',
        fuel: 'brændstof:',
        mileage: 'Kilometer:',
        engine: 'Motor:',
        body: 'Karosseri:',
        hash: 'Hash:',
        deleting: 'Sletter...',
        deleteVehicle: 'Slet køretøj',
        noVehicles: 'Ingen ejede køretøjer',
        identifiers: 'Identifikatorer',
        na: 'N/A'
      }
    : {
        noLicense: 'No license identifier found',
        failedCharacters: 'Failed to fetch characters',
        noCharacters: 'No characters found for this player',
        failedCharacterDetails: 'Failed to fetch character details',
        confirmDelete: 'Are you sure you want to delete vehicle with plate {plate}? This action cannot be undone!',
        failedDelete: 'Failed to delete vehicle',
        lookupVehicle: 'Lookup in Vehicle tab',
        deletedVehicle: 'Vehicle {plate} deleted successfully',
        title: 'Player Details',
        serverId: 'Server ID:',
        name: 'Name:',
        phoneNumber: 'Phone number:',
        ping: 'Ping:',
        selectCharacter: 'Select Character ({count} total)',
        loadingCharacterDetails: 'Loading character details...',
        characterInfo: 'Character Information',
        characterSlot: 'Character Slot:',
        fullName: 'Full Name:',
        dob: 'Date of Birth:',
        gender: 'Gender:',
        male: 'Male',
        female: 'Female',
        height: 'Height:',
        job: 'Job:',
        group: 'Group:',
        money: 'Money',
        bank: 'Bank',
        cash: 'Cash',
        blackMoney: 'Black Money',
        inventory: 'Inventory ({count} items)',
        previous: 'Previous',
        next: 'Next',
        pageInfo: 'Page {page} of {total} ({count} total)',
        noItems: 'No items in inventory',
        vehicles: 'Owned Vehicles ({count})',
        unknownVehicle: 'Unknown Vehicle',
        noPlate: 'NO PLATE',
        type: 'Type:',
        status: 'Status:',
        stored: 'Stored',
        out: 'Out',
        garage: 'Garage:',
        fuel: 'Fuel:',
        mileage: 'Mileage:',
        engine: 'Engine:',
        body: 'Body:',
        hash: 'Hash:',
        deleting: 'Deleting...',
        deleteVehicle: 'Delete Vehicle',
        noVehicles: 'No vehicles owned',
        identifiers: 'Identifiers',
        na: 'N/A'
      };

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const { apiFetch } = useAuth();

  useEffect(() => {
    fetchAllCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  const fetchAllCharacters = async () => {
    setLoading(true);
    setError(null);

    try {
      // Extract license identifier from player.identifiers array
      let licenseId = player.identifiers?.find(id => id.startsWith('license:'));
      
      if (!licenseId) {
        setError(t.noLicense);
        setLoading(false);
        return;
      }

      console.log('Fetching all characters for license:', licenseId);

      // Fetch all characters for this license
      const response = await apiFetch(`/api/player/characters/${encodeURIComponent(licenseId)}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedCharacters);
      }
      const characters = await response.json();
      console.log('Found characters:', characters.length);
      
      setAllCharacters(characters);
      
      // Auto-select first character if available
      if (characters.length > 0) {
        selectCharacter(characters[0]);
      } else {
        setError(t.noCharacters);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching characters:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const selectCharacter = async (character) => {
    // Always reset details when selecting a character to ensure fresh data
    setDetails(null);
    setSelectedCharacter(character);
    setLoading(true);
    setError(null);
    setVehiclePage(1); // Reset pagination when switching characters
    setInventoryPage(1);
    
    try {
      // Fetch detailed info for this specific character
      const response = await apiFetch(`/api/player/details/${encodeURIComponent(character.identifier)}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedCharacterDetails);
      }
      const data = await response.json();
      setDetails(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching character details:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getCharacterNumber = (identifier) => {
    const match = identifier.match(/^char(\d+):/);
    return match ? `Character ${match[1]}` : identifier;
  };

  const isStored = (vehicle) => {
    if (typeof vehicle?.isStored === 'boolean') return vehicle.isStored;
    return Number(vehicle?.stored) === 0;
  };



  const handleDeleteVehicle = async (plate) => {
    if (!window.confirm(text(t.confirmDelete, { plate }))) {
      return;
    }

    setDeletingVehicle(plate);

    try {
      const response = await apiFetch(`/api/vehicles/${encodeURIComponent(plate)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedDelete);
      }

      // Remove the deleted vehicle from the details
      setDetails(prev => ({
        ...prev,
        vehicles: prev.vehicles.filter(v => v.plate !== plate)
      }));

      alert(text(t.deletedVehicle, { plate }));
    } catch (err) {
      setError(err.message);
      alert(`Error: ${err.message}`);
    } finally {
      setDeletingVehicle(null);
    }
  };

  return (
    <div className="player-details">
      <div className="player-details-header">
        <h2>{t.title}</h2>
        <div className="player-basic-info">
          <div className="detail-row">
            <span className="label">{t.serverId}</span>
            <span className="value">{player.id}</span>
          </div>
          <div className="detail-row">
            <span className="label">{t.name}</span>
            <span className="value">{player.name}</span>
          </div>
          <div className="detail-row">
            <span className="label">{t.ping}</span>
            <span className="value badge-ping">{player.ping}ms</span>
          </div>
        </div>
      </div>

      {/* Character Selection */}
      {allCharacters.length > 0 && (
        <div className="character-selector">
          <h3>{text(t.selectCharacter, { count: allCharacters.length })}</h3>
          <div className="character-buttons">
            {allCharacters.map((char, index) => (
              <button
                key={index}
                className={`character-btn ${selectedCharacter?.identifier === char.identifier ? 'active' : ''}`}
                onClick={() => selectCharacter(char)}
              >
                <div className="char-btn-header">
                  <span className="char-number">{getCharacterNumber(char.identifier)}</span>
                  <span className="char-name">{char.firstname} {char.lastname}</span>
                </div>
                <div className="char-btn-info">
                  <span className="char-job">{char.job}</span>
                  <span className="char-money">{formatMoney(char.accounts?.bank || 0)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="loading-details">{t.loadingCharacterDetails}</div>}
      {error && <div className="error-details">{error}</div>}

      {details && selectedCharacter && (
        <div className="details-content">
          {/* Character Information */}
          <div className="details-section">
            <h3>{t.characterInfo}</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">{t.characterSlot}</span>
                <span className="value">{getCharacterNumber(details.identifier)}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.fullName}</span>
                <span className="value">{details.firstname} {details.lastname}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.dob}</span>
                <span className="value">{details.dateofbirth || t.na}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.phoneNumber}</span>
                <span className="value">{details.primaryPhoneNumber || details.phoneNumbers?.[0] || player.phone_number || player.primaryPhoneNumber || t.na}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.gender}</span>
                <span className="value">{details.sex === 'm' ? t.male : details.sex === 'f' ? t.female : t.na}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.height}</span>
                <span className="value">{details.height || t.na}</span>
              </div>
              <div className="info-item">
                <span className="label">{t.job}</span>
                <span className="value">{details.job} (Grade {details.job_grade})</span>
              </div>
              <div className="info-item">
                <span className="label">{t.group}</span>
                <span className="value badge-group">{details.group}</span>
              </div>
            </div>
          </div>

          {/* Money Information */}
          <div className="details-section">
            <h3>{t.money}</h3>
            <div className="money-grid">
              <div className="money-card bank">
                <div className="money-icon">B</div>
                <div className="money-info">
                  <span className="money-label">{t.bank}</span>
                  <span className="money-amount">{formatMoney(details.accounts?.bank || 0)}</span>
                </div>
              </div>
              <div className="money-card cash">
                <div className="money-icon">$</div>
                <div className="money-info">
                  <span className="money-label">{t.cash}</span>
                  <span className="money-amount">{formatMoney(details.accounts?.money || 0)}</span>
                </div>
              </div>
              {details.accounts?.black_money > 0 && (
                <div className="money-card black">
                  <div className="money-icon">$</div>
                  <div className="money-info">
                    <span className="money-label">{t.blackMoney}</span>
                    <span className="money-amount">{formatMoney(details.accounts.black_money)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Inventory */}
          <div className="details-section">
            <h3>{text(t.inventory, { count: details.inventory?.length || 0 })}</h3>
            {details.inventory && details.inventory.length > 0 ? (
              <>
                <div className="inventory-grid">
                  {details.inventory
                    .slice((inventoryPage - 1) * itemsPerPage, inventoryPage * itemsPerPage)
                    .map((item, index) => (
                    <div key={index} className="inventory-item">
                      <span className="item-name">{item.name}</span>
                      <span className="item-count">x{item.count || item.amount || 1}</span>
                    </div>
                  ))}
                </div>
                {details.inventory.length > itemsPerPage && (
                  <div className="pagination-controls">
                    <button 
                      onClick={() => setInventoryPage(prev => Math.max(1, prev - 1))}
                      disabled={inventoryPage === 1}
                      className="pagination-btn"
                    >
                      ← {t.previous}
                    </button>
                    <span className="pagination-info">
                      {text(t.pageInfo, {
                        page: inventoryPage,
                        total: Math.ceil(details.inventory.length / itemsPerPage),
                        count: details.inventory.length
                      })}
                    </span>
                    <button 
                      onClick={() => setInventoryPage(prev => Math.min(Math.ceil(details.inventory.length / itemsPerPage), prev + 1))}
                      disabled={inventoryPage >= Math.ceil(details.inventory.length / itemsPerPage)}
                      className="pagination-btn"
                    >
                      {t.next} →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-items">{t.noItems}</div>
            )}
          </div>

          {/* Owned Vehicles */}
          <div className="details-section">
            <h3>{text(t.vehicles, { count: details.vehicles?.length || 0 })}</h3>
            {details.vehicles && details.vehicles.length > 0 ? (
              <>
                <div className="vehicles-list">
                  {details.vehicles
                    .slice((vehiclePage - 1) * vehiclesPerPage, vehiclePage * vehiclesPerPage)
                    .map((vehicle, index) => (
                    <div key={index} className="vehicle-card">
                      <div className="vehicle-header">
                        <span className="vehicle-model">{vehicle.model || t.unknownVehicle}</span>
                        <span className="vehicle-plate">{vehicle.plate || t.noPlate}</span>
                      </div>
                      <div className="vehicle-details">
                        <div className="vehicle-stat">
                          <span className="stat-label">{t.type}</span>
                          <span className="stat-value">{vehicle.type || 'car'}</span>
                        </div>
                        <div className="vehicle-stat">
                          <span className="stat-label">{t.status}</span>
                          <span className={`stat-value ${isStored(vehicle) ? 'stored' : 'out'}`}>
                            {isStored(vehicle) ? `${t.stored}` : `${t.out}`}
                          </span>
                        </div>
                        <div className="vehicle-stat">
                          <span className="stat-label">{t.garage}</span>
                          <span className="stat-value">{vehicle.garage_id || t.na}</span>
                        </div>
                        <div className="vehicle-stat">
                          <span className="stat-label">{t.fuel}</span>
                          <span className="stat-value">{Math.round(vehicle.fuel) || 0}%</span>
                        </div>
                        <div className="vehicle-stat">
                          <span className="stat-label">{t.mileage}</span>
                          <span className="stat-value">{Math.round(vehicle.mileage || 0)} km</span>
                        </div>
                        {vehicle.engineHealth && (
                          <div className="vehicle-stat">
                            <span className="stat-label">{t.engine}</span>
                            <span className="stat-value">{vehicle.engineHealth}%</span>
                          </div>
                        )}
                        {vehicle.bodyHealth && (
                          <div className="vehicle-stat">
                            <span className="stat-label">{t.body}</span>
                            <span className="stat-value">{vehicle.bodyHealth}%</span>
                          </div>
                        )}
                        {vehicle.modelHash && (
                          <div className="vehicle-stat">
                            <span className="stat-label">{t.hash}</span>
                            <span className="stat-value">{vehicle.modelHash}</span>
                          </div>
                        )}
                      </div>
                      <div className="vehicle-actions">
                        <button
                          type="button"
                          onClick={() => onLookupVehicle && onLookupVehicle(vehicle.plate)}
                          className="lookup-vehicle-btn"
                          disabled={!vehicle.plate}
                        >
                          {t.lookupVehicle}
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleDeleteVehicle(vehicle.plate)}
                          className="delete-vehicle-btn"
                          disabled={deletingVehicle === vehicle.plate}
                        >
                          {deletingVehicle === vehicle.plate ? `🗑️ ${t.deleting}` : `🗑️ ${t.deleteVehicle}`}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {details.vehicles.length > vehiclesPerPage && (
                  <div className="pagination-controls">
                    <button 
                      onClick={() => setVehiclePage(prev => Math.max(1, prev - 1))}
                      disabled={vehiclePage === 1}
                      className="pagination-btn"
                    >
                      ← {t.previous}
                    </button>
                    <span className="pagination-info">
                      {text(t.pageInfo, {
                        page: vehiclePage,
                        total: Math.ceil(details.vehicles.length / vehiclesPerPage),
                        count: details.vehicles.length
                      })}
                    </span>
                    <button 
                      onClick={() => setVehiclePage(prev => Math.min(Math.ceil(details.vehicles.length / vehiclesPerPage), prev + 1))}
                      disabled={vehiclePage >= Math.ceil(details.vehicles.length / vehiclesPerPage)}
                      className="pagination-btn"
                    >
                      {t.next} →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-items">{t.noVehicles}</div>
            )}
          </div>

          {/* Identifiers */}
          <div className="details-section">
            <h3>{t.identifiers}</h3>
            <div className="identifiers-list">
              {player.identifiers?.map((identifier, index) => (
                <div key={index} className="identifier-badge">
                  {identifier}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayerDetails;



